import { useUserIdentityContext } from "@core/services/user-identity.context";
import CustomerCard from "@core/ui/customer-card/customer-card";
import { GridItemModel } from "@core/ui/grid-item/grid-item";
import { ActionEnum } from "@factory/model/actions.enum";
import {
  GetCardSeriesExtendDto,
  GetCardSeriesModel,
  GetResponseCardSeriesModel,
} from "@factory/model/query-model-card-series";
import {
  GetResponseRewardModel,
  RewardBalance,
} from "@factory/model/query-model-reward";
import { ScanMethod } from "@factory/model/scan-method.enum";
import { useQyGetCardSeries } from "@factory/query/card-series.query";
import { useQyGetGoogleCloudAssetPreview } from "@factory/query/google-cloud-storage.query";
import { useQyGetRewardBalance } from "@factory/query/reward.query";
import { ButtonAction } from "@shared/ui/header-button/button-action";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";

import { usePrintableCard } from "./printable-card.hook";

export function useListCards() {
  // LOCAL DEFINITION
  const { downloadPng, downloadPdf, cardElement } = usePrintableCard();
  const navigate = useNavigate();
  const { getUserRole, getAuthUser } = useUserIdentityContext();
  const { showError } = useNotificationContext();
  const { tenant } = getUserRole();
  const { userCode } = getAuthUser();
  const [selectedCard, setSelectedCard] = useState<GetCardSeriesModel>();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [avatar, setAvatar] = useState<string>("");
  const { mutate: viewAsset } = useQyGetGoogleCloudAssetPreview();
  const [displayMode, setDisplayMode] = useState<ScanMethod>(ScanMethod.QR);
  const [previewRedeem, setPreviewRedeem] = useState<GridItemModel | null>(
    null
  );
  const [balanceData, setBalanceData] = useState<RewardBalance | undefined>(
    undefined
  );
  const actions: ButtonAction[] = [
    { label: "Download", type: "split", value: ActionEnum.DownloadPNG },
    { label: "Download PDF", type: "more", value: ActionEnum.DownloadPDF },
    { label: "Card Settings", type: "more", value: ActionEnum.CardSettings },
  ];

  // API DEFINITION
  const handleCardSeriesSuccess = (data: GetResponseCardSeriesModel) => {
    if (!data || data.data?.length === 0) {
      return;
    }
    const responseData = data.data?.[0];
    setSelectedCard(responseData as GetCardSeriesExtendDto);
  };
  const { data: cardsResponse } = useQyGetCardSeries(
    "",
    9999,
    0,
    {},
    { user_code: userCode },
    !!userCode,
    handleCardSeriesSuccess
  );
  const cards = (cardsResponse?.data as GetCardSeriesModel[]) || [];

  const handleBalanceOnSuccess = (response: GetResponseRewardModel) => {
    const data = response as RewardBalance;
    if (data?.level_avatar) {
      viewAsset(data?.level_avatar, {
        onSuccess(data) {
          const assetDisplay = URL.createObjectURL(data);
          setAvatar(assetDisplay);
        },
      });
    }
    setBalanceData(data);
  };
  useQyGetRewardBalance(
    selectedCard?.card_alias || "",
    tenant,
    !!selectedCard?.card_alias,
    handleBalanceOnSuccess
  );

  // LOCAL FUNCTION
  const nextCard = () => {
    if (selectedIndex < cards.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    }
  };
  const previousCard = () => {
    if (selectedIndex >= 1) {
      setSelectedIndex(selectedIndex - 1);
    }
  };
  const toggleDisplay = () => {
    switch (displayMode) {
      case ScanMethod.QR:
        setDisplayMode(ScanMethod.Barcode);
        break;
      case ScanMethod.Barcode:
        setDisplayMode(ScanMethod.QR);
        break;
      default:
        setDisplayMode(ScanMethod.QR);
        break;
    }
  };
  const handleAction = (action: ButtonAction) => {
    if (!balanceData) {
      showError("No balance data available for this card.");
      return;
    }
    switch (action.value) {
      case ActionEnum.DownloadPNG:
        downloadPng(balanceData?.card_alias ?? '');
        break;
      case ActionEnum.DownloadPDF:
        downloadPdf(balanceData?.card_alias ?? '');
        break;
      case ActionEnum.CardSettings:
        navigate(`card-settings`);
        break;
    }
  };

  const cardsElement = (
    <div>
      {cards && cards?.length > 0 ? (
        <div className="flex space-x-5 items-center min-h-[40vh] py-2">
          <CustomerCard
            placement="left"
            hidden={selectedIndex === 0}
            onClickFn={previousCard}
          />
          <CustomerCard
            balanceData={balanceData}
            avatar={avatar}
            shouldShowCard={true}
            onChangeDisplay={(mode) => setDisplayMode(mode)}
          />
          <CustomerCard
            placement="right"
            onClickFn={nextCard}
            hidden={selectedIndex === cards?.length - 1}
          />
          {cardElement(balanceData as RewardBalance, displayMode)}
        </div>
      ) : (
        <div className="flex space-x-5 justify-center items-center min-h-[40vh] py-2">
          {/* <CustomerCard data={{ card_alias: "Add a new Card" }} /> */}
        </div>
      )}
    </div>
  );
  const cardDetailsElement = (
    <div>
      <Outlet />
    </div>
  );

  return {
    cards,
    selectedCard,
    cardsElement,
    cardDetailsElement,
    displayMode,
    actions,
    balanceData,
    previewRedeem,
    tenant,
    navigate,
    setPreviewRedeem,
    setDisplayMode,
    toggleDisplay,
    handleAction,
    setSelectedCard,
  };
}
